import { NotificationAlert, Table } from '../../../components';
import { Columns } from './columns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getAllUsers } from '../../../services/userServices';
import {
  AllReviewsProps,
  EmployeeData,
  MenteesProps,
  ObjectiveReport,
} from '../../../types';
import {
  Button,
  DatePicker,
  DatePickerProps,
  Input,
  Modal,
  Select,
  Spin,
} from 'antd';
import { SearchProps } from 'antd/es/input';
import { DownloadOutlined, MoreOutlined } from '@ant-design/icons';
import { exportToExcel } from './helper';
import dayjs from 'dayjs';
import { getObjectivesReport } from '../../../services/objectiveServices';
import { getAllQuarters } from '../../../services/reviewsServices';
import { AnnualReviewCycle } from '../../../types/AnnualReview';
import { getAllAnnualReviewCycles } from '../../../services/annualServices';
import Chart from '../../../components/Chart';
import {
  getHRDashboardStatus,
  getHRDashboardStatusFilter,
  ResponseHRDashboard,
} from '../../../services/dashboardService';
import { Dayjs } from 'dayjs';

const { Search } = Input;
const { Option } = Select;
let searchtimer: ReturnType<typeof setTimeout>;

type SelectedViewMoreType =
  | 'Annual Review Submissions'
  | 'All Objectives'
  | 'Development Objective'
  | 'Total Feedback';

// Create a separate component for the Average Employee Competency Analysis
const AverageEmployeeCompetencyChart = ({
  selectedDate,
  selectedDisciplinesOption,
  selectedTier,
  disciplinesOptions,
  tierOptions,
  handleDisciplineChange,
  handleTierChange,
}: {
  selectedDate: Dayjs | null;
  selectedDisciplinesOption: { disciplineId: number; discipline: string };
  selectedTier: number;
  disciplinesOptions: Array<{ disciplineId: number; discipline: string }>;
  tierOptions: number[];
  handleDisciplineChange: (id: number) => void;
  handleTierChange: (value: number) => void;
}) => {
  const [chartLoading, setChartLoading] = useState(false);
  const [competencyChartData, setCompetencyChartData] = useState<
    Array<{
      competency: string;
      rating: number;
      hoverMessages: string[];
    }>
  >([]);
  const myRef = useRef<any>();

  const fetchCompetencyData = async () => {
    setChartLoading(true);
    const year = selectedDate?.year();
    if (!year) {
      setChartLoading(false);
      return;
    }

    if (
      selectedDisciplinesOption.disciplineId === undefined ||
      selectedTier === undefined
    ) {
      setChartLoading(false);
      return;
    }

    try {
      const { data, error } = await getHRDashboardStatusFilter(
        year,
        selectedDisciplinesOption.disciplineId,
        selectedTier
      );

      if (error) {
        myRef.current?.call('error', error?.message);
        return;
      }

      if (data?.competencyLevelPerformance) {
        const newChartData = data.competencyLevelPerformance.map((item) => ({
          competency: item.competencyName,
          rating: item.average,
          hoverMessages: [
            `Highest Score: ${item.highestScoringMentee || 'N/A'}`,
            `Lowest Score: ${item.lowestScoringMentee || 'N/A'}`,
          ],
        }));
        setCompetencyChartData(newChartData);
      }
    } catch (error) {
      myRef.current?.call('error', 'Failed to fetch competency data');
    } finally {
      setChartLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDate?.year()) {
      fetchCompetencyData();
    }
  }, [selectedDisciplinesOption.disciplineId, selectedTier, selectedDate]);

  return (
    <div className="border p-4 rounded-sm">
      <div className="p-[10px] flex items-center justify-between">
        <div>
          <div className="text-[20px] font-[500]">
            Average Employee Competency Analysis
          </div>
          <div className="opacity-45">
            Average review ratings for competency given by mentors for each
            mentee
          </div>
        </div>
        <div className="flex gap-x-4">
          <Select
            placeholder="Discipline"
            onChange={handleDisciplineChange}
            value={selectedDisciplinesOption.disciplineId}
            style={{ width: 180 }}
          >
            {disciplinesOptions.map(({ discipline, disciplineId }) => (
              <Option key={disciplineId} value={disciplineId}>
                {discipline}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="Tier"
            onChange={handleTierChange}
            value={selectedTier}
            style={{ width: 120 }}
          >
            {tierOptions.map((value, index) => (
              <Option key={index} value={index}>
                {value === 0 ? 'All' : 'Tier ' + value}
              </Option>
            ))}
          </Select>
        </div>
      </div>
      {chartLoading ? (
        <div className="flex justify-center items-center h-[500px]">
          <Spin size="large" />
        </div>
      ) : (
        <Chart
          type="bar"
          data={competencyChartData}
          title=""
          stepSize={0.5}
          maxValue={5.5}
          showLegend={false}
          height={500}
          isDecimal={true}
        />
      )}
      <NotificationAlert ref={myRef} />
    </div>
  );
};

const EmployeeDashboard = () => {
  const [data, setData] = useState<EmployeeData[]>([]);
  const [filtered, setFiltered] = useState<EmployeeData[]>([]);
  const [loading, setLoading] = useState(true);
  const myRef = useRef<any>();
  const [exportLoading, setExportLoading] = useState(false);
  const [hrStats, setHRStatus] = useState<ResponseHRDashboard | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [
    selectedStatusOfAnnualReviewSubmission,
    setSelectedStatusOfAnnualReviewSubmission,
  ] = useState({ name: 'All', id: 0 });
  const [selectedStatusOfAllObjectives, setSelectedStatusOfAllObjectives] =
    useState({ name: 'All', id: 0 });
  const [selectedStatusOfDevelopment, setSelectedStatusOfDevelopment] =
    useState({ name: 'All', id: 0 });

  const [selectedDisciplinesOption, setSelectedDisciplinesOption] = useState({
    discipline: 'All',
    disciplineId: 0,
  });
  const [selectedTier, setSelectedTier] = useState(0);

  const [selectedViewMore, setSelectedViewMore] =
    useState<SelectedViewMoreType>('Annual Review Submissions');

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());

  const [hrDashboardLoading, setHrDashboardLoading] = useState(true);

  const fetchUsers = async (): Promise<MenteesProps[] | undefined> => {
    const { data, error } = await getAllUsers('hr');
    if (error) {
      myRef.current?.call('error', error?.message);
      return undefined;
    }
    return data;
  };

  const fetchReviews = async (): Promise<AllReviewsProps[] | undefined> => {
    const { data, error } = await getAllQuarters('hr');
    if (error) {
      myRef.current?.call('error', error?.message);
      return undefined;
    }
    return data;
  };

  const fetchAnnualCycles = async (): Promise<
    AnnualReviewCycle[] | undefined
  > => {
    const { data, error } = await getAllAnnualReviewCycles('hr');
    if (error) {
      myRef.current?.call('error', error?.message);
      return undefined;
    }
    return data;
  };

  const fetchQuarterCycles = async (): Promise<
    AllReviewsProps[] | undefined
  > => {
    const { data, error } = await getAllQuarters('hr');
    if (error) {
      myRef.current?.call('error', error?.message);
      return undefined;
    }
    return data;
  };

  const bindEmployeeAndReviewData = (
    empData: MenteesProps[],
    reviewData: AllReviewsProps[],
    annualCycleData: AnnualReviewCycle[],
    quarterCycleData: AllReviewsProps[]
  ) => {
    const data: EmployeeData[] = empData.map((employee) => {
      return {
        ...employee,
        cycleId:
          reviewData.find(
            (review) =>
              dayjs(dayjs().toDate()).isAfter(review.startDate) &&
              dayjs(dayjs().toDate()).isBefore(review.endDate)
          )?.id || '',
        cycleName:
          reviewData.find(
            (review) =>
              dayjs(dayjs().toDate()).isAfter(review.startDate) &&
              dayjs(dayjs().toDate()).isBefore(review.endDate)
          )?.name || '',
        annualData: annualCycleData.filter((cycle) => cycle.status >= 1),
        quarterData: quarterCycleData.filter((cycle) => cycle.status >= 1),
      };
    });
    setData(data);
    setFiltered(data);
  };

  const fetchHRDashboard = async () => {
    setHrDashboardLoading(true);
    if (!selectedDate) return;
    const year = selectedDate?.year();
    const { data, error } = await getHRDashboardStatus(year);
    if (error) {
      myRef.current?.call('error', error?.message);
      setHrDashboardLoading(false);
      return undefined;
    }

    setHRStatus(data);
    setHrDashboardLoading(false);
    return data;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [empData, reviewData, annualCycleData, quarterCycleData] =
        await Promise.all([
          fetchUsers(),
          fetchReviews(),
          fetchAnnualCycles(),
          fetchQuarterCycles(),
        ]);

      if (!empData || !reviewData || !annualCycleData || !quarterCycleData) {
        throw new Error('Failed to fetch required data');
      }

      bindEmployeeAndReviewData(
        empData,
        reviewData,
        annualCycleData,
        quarterCycleData
      );
    } catch (error) {
      myRef.current?.call('error', 'Failed to load dashboard data');
      console.error('Dashboard loading error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      if (mounted) {
        await fetchData();
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      await fetchHRDashboard();
    };

    fetchDashboardData();
  }, [selectedDate]);

  const tierOptions = useMemo(() => {
    return [0, ...(hrStats?.overallCompetencyAnalysis.tiers ?? [])];
  }, [hrStats?.overallCompetencyAnalysis.tiers]);

  const disciplinesOptions = useMemo(() => {
    return [
      { disciplineId: 0, discipline: 'All' },
      ...(hrStats?.overallCompetencyAnalysis.discipline ?? []),
    ];
  }, [hrStats?.overallCompetencyAnalysis.discipline]);

  const statusOfAllObjectivesOptions = useMemo(() => {
    const defaultOptions = [
      {
        name: 'All',
        id: 0,
      },
    ];

    if (!hrStats?.activeObjectivesStatus) return defaultOptions;

    const options = hrStats.activeObjectivesStatus.mentorObjectives.map(
      (mentor, id) => ({
        name: mentor.mentorName,
        id: id + 1,
      })
    );

    return [defaultOptions[0], ...options];
  }, [hrStats?.activeObjectivesStatus]);

  const statusOfAnnualReviewSubmissionOptions = useMemo(() => {
    const defaultOptions = [
      {
        name: 'All',
        id: 0,
      },
    ];

    if (!hrStats?.annualReviewSubmissionStatus) return defaultOptions;

    const options = hrStats.annualReviewSubmissionStatus.mentorStats.map(
      (mentor, index) => ({
        name: mentor.mentorName,
        id: index + 1,
      })
    );

    return [defaultOptions[0], ...options];
  }, [hrStats?.annualReviewSubmissionStatus]);

  const statusOfDevelopmentOptions = useMemo(() => {
    const defaultOptions = [
      {
        name: 'All',
        id: 0,
      },
    ];

    if (!hrStats?.developmentObjectivesStatus) return defaultOptions;

    const options = hrStats.developmentObjectivesStatus.mentorObjectives.map(
      (mentor, index) => ({
        name: mentor.mentorName,
        id: index + 1,
      })
    );

    return [defaultOptions[0], ...options];
  }, [hrStats?.developmentObjectivesStatus]);

  const statusOfAllObjectivesData = useMemo(() => {
    const allData = {
      name: 'All',
      completed: hrStats?.activeObjectivesStatus.completedObjectives || 0,
      draft: hrStats?.activeObjectivesStatus.draftObjectives || 0,
      inprogress: hrStats?.activeObjectivesStatus.inProgressObjectives || 0,
      notStarted: hrStats?.activeObjectivesStatus.notStartedObjectives || 0,
      total: hrStats?.activeObjectivesStatus.totalObjectives || 0,
      // Get all completed mentees from developmentObjectivesStatus.mentorObjectives
      completedMentees:
        hrStats?.activeObjectivesStatus.mentorObjectives.flatMap(
          (mentor) => mentor.completedMenteeList
        ) || [],
      // Get all not started mentees from developmentObjectivesStatus.mentorObjectives
      notStartedMentees:
        hrStats?.activeObjectivesStatus.mentorObjectives.flatMap(
          (mentor) => mentor.notStartedMenteeList
        ) || [],
    };

    const data =
      hrStats?.activeObjectivesStatus.mentorObjectives.map((mentor) => ({
        name: mentor.mentorName,
        completed: mentor.completedObjectives,
        draft: mentor.draftObjectives,
        inprogress: mentor.inProgressObjectives,
        notStarted: mentor.notStartedObjectives,
        total: mentor.totalObjectives,
        completedMentees: mentor.completedMenteeList,
        notStartedMentees: mentor.notStartedMenteeList,
      })) ?? [];

    return [allData, ...data];
  }, [hrStats?.activeObjectivesStatus]);

  const statusOfAnnualReviewSubmissionData = useMemo(() => {
    const allData = {
      name: 'All',
      completed: hrStats?.annualReviewSubmissionStatus.completedReviews || 0,
      pending: hrStats?.annualReviewSubmissionStatus.pendingReviews || 0,
      total: hrStats?.annualReviewSubmissionStatus.totalReviews || 0,
      // total from hrStats?.annualReviewSubmissionStatus.mentorStats completedMenteesList
      completedMentees:
        hrStats?.annualReviewSubmissionStatus.mentorStats.flatMap(
          (mentor) => mentor.completedMenteesList
        ) || [],
      // total from hrStats?.annualReviewSubmissionStatus.mentorStats pendingMenteesList
      pendingMentees:
        hrStats?.annualReviewSubmissionStatus.mentorStats.flatMap(
          (mentor) => mentor.pendingMenteesList
        ) || [],
    };

    const data =
      hrStats?.annualReviewSubmissionStatus.mentorStats.map((mentor) => ({
        name: mentor.mentorName,
        completed: mentor.completedReviews,
        pending: mentor.pendingReviews,
        total: mentor.totalReviews,
        completedMentees: mentor.completedMenteesList,
        pendingMentees: mentor.pendingMenteesList,
      })) ?? [];

    return [allData, ...data];
  }, [hrStats?.annualReviewSubmissionStatus]);

  const statusOfDevelopmentData = useMemo(() => {
    const allData = {
      name: 'All',
      completed: hrStats?.developmentObjectivesStatus.completedObjectives || 0,
      draft: hrStats?.developmentObjectivesStatus.draftObjectives || 0,
      inprogress:
        hrStats?.developmentObjectivesStatus.inProgressObjectives || 0,
      notStarted:
        hrStats?.developmentObjectivesStatus.notStartedObjectives || 0,
      total: hrStats?.developmentObjectivesStatus.totalObjectives || 0,
      // Get all completed mentees from developmentObjectivesStatus.mentorObjectives
      completedMentees:
        hrStats?.developmentObjectivesStatus.mentorObjectives.flatMap(
          (mentor) => mentor.completedMenteeList
        ) || [],
      // Get all not started mentees from developmentObjectivesStatus.mentorObjectives
      notStartedMentees:
        hrStats?.developmentObjectivesStatus.mentorObjectives.flatMap(
          (mentor) => mentor.notStartedMenteeList
        ) || [],
    };

    const data =
      hrStats?.developmentObjectivesStatus.mentorObjectives.map((mentor) => ({
        name: mentor.mentorName,
        completed: mentor.completedObjectives,
        draft: mentor.draftObjectives,
        inprogress: mentor.inProgressObjectives,
        notStarted: mentor.notStartedObjectives,
        total: mentor.totalObjectives,
        completedMentees: mentor.completedMenteeList,
        notStartedMentees: mentor.notStartedMenteeList,
      })) ?? [];

    return [allData, ...data];
  }, [hrStats?.developmentObjectivesStatus]);

  const filterData = (value: string) =>
    data?.filter(
      (item) =>
        item?.name?.toLocaleLowerCase().includes(value) ||
        item?.designation?.toLocaleLowerCase().includes(value) ||
        item?.tier?.toString()?.toLocaleLowerCase().includes(value)
    );

  const onSearch: SearchProps['onSearch'] = (value) => {
    setLoading(true);
    value = value?.toLocaleLowerCase();
    const filter = filterData(value);
    setFiltered(filter);
    setLoading(false);
  };

  const handleChange = (value: string) => {
    if (value !== '') {
      clearTimeout(searchtimer);
      searchtimer = setTimeout(() => {
        value = value?.toLocaleLowerCase();
        const filter = filterData(value);
        setFiltered(filter);
      }, 600);
    } else {
      clearTimeout(searchtimer);
      setFiltered(data);
    }
  };

  const getReportData = async (): Promise<ObjectiveReport[] | undefined> => {
    const response = await getObjectivesReport();
    if (response.error)
      return myRef.current?.call('error', response.error?.message);
    if (response.data) return response.data;
  };

  const handleExportData = async () => {
    setExportLoading(true);
    const reportData = await getReportData();
    if (reportData) {
      exportToExcel(
        reportData?.map((item) => {
          return {
            menteeName: item.menteeName,
            mentorName: item.mentorName,
            objectiveType: item.objectiveType,
            description: item.description,
            status: item.status,
            expectedOutcome: item.expectedOutcome,
            startDate: item.startDate,
            endDate: item.endDate,
          };
        }),
        data.map((item) => {
          return {
            name: item.name,
            designation: item.designation,
            tier: item.tier,
            mentor: item.mentor.name,
            objectiveCount: item.objectiveCount,
            lastAccessed: item.lastAccessedDate ? item.lastAccessedDate : '',
          };
        }),
        `Employee_Details_and_Objectives_Report - ${dayjs().format('MMM DD YYYY')}`
      );
    }
    setExportLoading(false);
  };

  const averageScoreDisciplinesData = useMemo(() => {
    const disciplineAverageMap = new Map(
      hrStats?.averageScoreAcrossDisciplines.disciplineAverage.map((avg) => [
        avg.disciplineId,
        avg.average,
      ]) || []
    );

    const disciplinePerformanceMap = new Map(
      hrStats?.averageScoreAcrossDisciplines.disciplineLevelPerformance.map(
        (perf) => [perf.disciplineId, perf]
      ) || []
    );

    const data = (hrStats?.averageScoreAcrossDisciplines.disciplines ?? []).map(
      (d) => ({
        competency: d.discipline,
        rating: disciplineAverageMap.get(d.disciplineId) || 0,
        hoverMessages: [
          'Highest Score: ' +
            (disciplinePerformanceMap.get(d.disciplineId)
              ?.highestScoringMentee || 'N/A'),
          'Lowest Score: ' +
            (disciplinePerformanceMap.get(d.disciplineId)
              ?.lowestScoringMentee || 'N/A'),
        ],
      })
    );

    return data;
  }, [hrStats?.averageScoreAcrossDisciplines]);

  const handleDisciplineChange = (id: number) => {
    const existingDisciplinesOption = disciplinesOptions.find(
      (d) => d.disciplineId === id
    );
    setSelectedDisciplinesOption(
      existingDisciplinesOption ?? {
        disciplineId: 0,
        discipline: 'All',
      }
    );
    // Add logic to filter data based on selected discipline
  };

  const handleSelectStatusOfAllObjectives = (id: number) => {
    setSelectedStatusOfAllObjectives(statusOfAllObjectivesOptions[id]);
  };

  const handleSelectStatusOfAnnualReviewSubmission = (id: number) => {
    setSelectedStatusOfAnnualReviewSubmission(
      statusOfAnnualReviewSubmissionOptions[id]
    );
  };

  const handleSelectStatusOfDevelopment = (id: number) => {
    setSelectedStatusOfDevelopment(statusOfDevelopmentOptions[id]);
  };

  const handleTierChange = (value: number) => {
    setSelectedTier(value);
  };

  const showModal = (type: SelectedViewMoreType) => {
    setSelectedViewMore(type);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  // date change function
  const dateChange: DatePickerProps['onChange'] = (date) => {
    setSelectedDate(date);
  };

  return (
    <>
      <div className="flex flex-col gap-y-[10px]">
        <div className="p-[10px] flex flex-col mb-[-10px]">
          <div className="flex justify-between items-center">
            <div className="text-[20px] font-[500]">Employee Dashboard</div>
            <DatePicker
              onChange={dateChange}
              value={selectedDate}
              picker="year"
              className="ml-3 w-fit"
              placeholder="Year"
            />
          </div>
          <div className="opacity-45">
            Effortlessly manage your organization's talent! Navigate employee
            profiles, tier classifications, and managerial hierarchies for
            streamlined HR oversight and strategic talent development. Moreover,
            you can click to download an Excel report with employee details,
            including names, designations, tiers, mentor names, and set
            objectives for focused performance analysis.
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <AverageEmployeeCompetencyChart
            selectedDate={selectedDate}
            selectedDisciplinesOption={selectedDisciplinesOption}
            selectedTier={selectedTier}
            disciplinesOptions={disciplinesOptions}
            tierOptions={tierOptions}
            handleDisciplineChange={handleDisciplineChange}
            handleTierChange={handleTierChange}
          />

          <Modal
            title={
              selectedViewMore === 'Total Feedback'
                ? 'Total quarterly feedback deadlines missed'
                : selectedViewMore === 'Annual Review Submissions'
                  ? 'Status of Annual Review Submissions'
                  : selectedViewMore === 'All Objectives'
                    ? 'Status of Active Objectives'
                    : 'Status of Development Objectives'
            }
            open={isModalVisible}
            onCancel={handleCancel}
            footer={null}
          >
            <div className="flex justify-between">
              <div>
                <h2>
                  <strong>
                    {selectedViewMore === 'Total Feedback'
                      ? 'Mentors'
                      : 'Completed'}
                  </strong>
                </h2>
                <ul>
                  {selectedViewMore === 'Total Feedback'
                    ? // Total Feedback view
                      hrStats?.totalQuarterlyFeedbackDeadlinesMissedCount.mentors.map(
                        (mentor, index) => <li key={index}>{mentor}</li>
                      )
                    : selectedViewMore === 'Annual Review Submissions'
                      ? // Annual Review Submissions view
                        statusOfAnnualReviewSubmissionData[
                          selectedStatusOfAnnualReviewSubmission.id
                        ].completedMentees.map((mentee, index) => (
                          <li key={index}>{mentee}</li>
                        ))
                      : selectedViewMore === 'All Objectives'
                        ? // All Objectives view
                          statusOfAllObjectivesData[
                            selectedStatusOfAllObjectives.id
                          ].completedMentees.map((mentee, index) => (
                            <li key={index}>{mentee.menteeName}</li>
                          ))
                        : // Development Objectives view
                          statusOfDevelopmentData[
                            selectedStatusOfDevelopment.id
                          ].completedMentees.map((mentee, index) => (
                            <li key={index}>{mentee.menteeName}</li>
                          ))}
                </ul>
              </div>
              <div>
                <h2>
                  <strong>
                    {selectedViewMore === 'Total Feedback'
                      ? 'Mentees'
                      : selectedViewMore === 'Annual Review Submissions'
                        ? 'Pending'
                        : 'Not-Started'}
                  </strong>
                </h2>
                <ul>
                  {selectedViewMore === 'Total Feedback'
                    ? // Total Feedback view
                      hrStats?.totalQuarterlyFeedbackDeadlinesMissedCount.mentees.map(
                        (mentee, index) => <li key={index}>{mentee}</li>
                      )
                    : selectedViewMore === 'Annual Review Submissions'
                      ? // Annual Review Submissions view
                        statusOfAnnualReviewSubmissionData[
                          selectedStatusOfAnnualReviewSubmission.id
                        ].pendingMentees.map((mentee, index) => (
                          <li key={index}>{mentee}</li>
                        ))
                      : selectedViewMore === 'All Objectives'
                        ? // All Objectives view
                          statusOfAllObjectivesData[
                            selectedStatusOfAllObjectives.id
                          ].notStartedMentees.map((mentee, index) => (
                            <li key={index}>{mentee.menteeName}</li>
                          ))
                        : // Development Objectives view
                          statusOfDevelopmentData[
                            selectedStatusOfDevelopment.id
                          ].notStartedMentees.map((mentee, index) => (
                            <li key={index}>{mentee.menteeName}</li>
                          ))}
                </ul>
              </div>
            </div>
          </Modal>

          <div className="border p-4">
            <div className="p-[10px] flex items-center justify-between">
              <div className="text-[20px] font-[500]">
                Status of Annual Review Submissions
              </div>
              <div className="flex gap-x-4">
                <Select
                  placeholder="Mentor"
                  value={selectedStatusOfAnnualReviewSubmission.id}
                  onChange={handleSelectStatusOfAnnualReviewSubmission}
                  style={{ width: 200 }}
                >
                  {statusOfAnnualReviewSubmissionOptions.map(({ name, id }) => (
                    <Option key={id} value={id}>
                      {name}
                    </Option>
                  ))}
                </Select>

                <Button
                  type="text"
                  icon={<MoreOutlined />}
                  onClick={() => showModal('Annual Review Submissions')}
                  style={{ cursor: 'pointer', float: 'right' }}
                />
              </div>
            </div>
            {!hrDashboardLoading && (
              <Chart
                type="doughnut"
                totalObjectives={
                  statusOfAnnualReviewSubmissionData[
                    selectedStatusOfAnnualReviewSubmission.id
                  ].total
                }
                centerText="Reviews"
                data={[
                  {
                    label: 'Completed',
                    value:
                      statusOfAnnualReviewSubmissionData[
                        selectedStatusOfAnnualReviewSubmission.id
                      ].completed,
                    color: '#9BDFC4',
                  },
                  {
                    label: 'Pending',
                    value:
                      statusOfAnnualReviewSubmissionData[
                        selectedStatusOfAnnualReviewSubmission.id
                      ].pending,
                    color: '#62B2FD',
                  },
                ]}
                height={300}
              />
            )}
          </div>
        </div>

        <div className="p-[10px] flex flex-col mb-[-10px]">
          <div className="text-[20px] font-[500]">
            Average score across disciplines
          </div>
          <div className="opacity-45">
            Average feedback score across disciplines
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="border rounded-sm p-4">
            <Chart
              type="bar"
              data={averageScoreDisciplinesData}
              showAllPercentages
              showLegend={false}
              title=""
              maxValue={100}
              height={300}
            />
          </div>

          <div className="border p-4">
            <div className="p-[10px] flex items-center justify-between">
              <div className="text-[20px] font-[500]">
                Status of All Objectives
              </div>
              <div className="flex gap-x-4">
                <Select
                  placeholder="Mentor"
                  onChange={handleSelectStatusOfAllObjectives}
                  value={selectedStatusOfAllObjectives.id}
                  style={{ width: 200 }}
                >
                  {statusOfAllObjectivesOptions.map(({ name, id }) => (
                    <Option key={id} value={id}>
                      {name}
                    </Option>
                  ))}
                </Select>

                <Button
                  type="text"
                  icon={<MoreOutlined />}
                  onClick={() => showModal('All Objectives')}
                  style={{ cursor: 'pointer', float: 'right' }}
                />
              </div>
            </div>
            {!hrDashboardLoading && (
              <Chart
                customColors={['#9BDFC4', '#FD9C13', '#9C27B0', '#62B2FD']}
                type="doughnut"
                centerText="Objectives"
                totalObjectives={
                  statusOfAllObjectivesData[selectedStatusOfAllObjectives.id]
                    .total
                }
                data={[
                  {
                    label: 'Completed',
                    value:
                      statusOfAllObjectivesData[
                        selectedStatusOfAllObjectives.id
                      ].completed,
                    color: '#9BDFC4',
                  },
                  {
                    label: 'In Progress',
                    value:
                      statusOfAllObjectivesData[
                        selectedStatusOfAllObjectives.id
                      ].inprogress,
                    color: '#FD9C13',
                  },
                  {
                    label: 'Draft',
                    value:
                      statusOfAllObjectivesData[
                        selectedStatusOfAllObjectives.id
                      ].draft,
                    color: '#9C27B0',
                  },
                  {
                    label: 'Not Started',
                    value:
                      statusOfAllObjectivesData[
                        selectedStatusOfAllObjectives.id
                      ].notStarted,
                    color: '#62B2FD',
                  },
                ]}
                height={300}
              />
            )}
          </div>
        </div>

        <div className="p-[10px] flex flex-col mb-[-10px]">
          <div className="grid grid-cols-2">
            <div className="border p-4">
              <div className="p-[10px] flex items-center justify-between">
                <div className="text-[20px] font-[500]">
                  Status of Development Objectives
                </div>
                <div className="flex gap-x-4">
                  <Select
                    placeholder="Mentor"
                    onChange={handleSelectStatusOfDevelopment}
                    value={selectedStatusOfDevelopment.id}
                    style={{ width: 200 }}
                  >
                    {statusOfDevelopmentOptions.map(({ name, id }) => (
                      <Option key={id} value={id}>
                        {name}
                      </Option>
                    ))}
                  </Select>

                  <Button
                    type="text"
                    icon={<MoreOutlined />}
                    onClick={() => showModal('Development Objective')}
                    style={{ cursor: 'pointer', float: 'right' }}
                  />
                </div>
              </div>
              {!hrDashboardLoading && (
                <Chart
                  type="doughnut"
                  centerText="Objectives"
                  totalObjectives={
                    statusOfDevelopmentData[selectedStatusOfDevelopment.id]
                      .total
                  }
                  data={[
                    {
                      label: 'Completed',
                      value:
                        statusOfDevelopmentData[selectedStatusOfDevelopment.id]
                          .completed,
                      color: '#9BDFC4',
                    },
                    {
                      label: 'In Progress',
                      value:
                        statusOfDevelopmentData[selectedStatusOfDevelopment.id]
                          .inprogress,
                      color: '#FD9C13',
                    },
                    {
                      label: 'Draft',
                      value:
                        statusOfDevelopmentData[selectedStatusOfDevelopment.id]
                          .draft,
                      color: '#9C27B0',
                    },
                    {
                      label: 'Not Started',
                      value:
                        statusOfDevelopmentData[selectedStatusOfDevelopment.id]
                          .notStarted,
                      color: '#62B2FD',
                    },
                  ]}
                  height={300}
                />
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-row justify-between items-center w-full mb-2 ">
          <div className="ml-[10px]">
            <Search
              placeholder="Search Name / Designation / Tier"
              onSearch={onSearch}
              className="w-[280px] ml-auto"
              onChange={(e) => handleChange(e.target.value)}
            />
          </div>
          <div className="mr-[10px]">
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportData}
              loading={exportLoading}
            >
              Export to Excel
            </Button>
          </div>
        </div>

        <Table
          columns={Columns}
          data={filtered}
          errorFetchingData={false}
          loading={loading}
          rowKey="id"
        />
        <NotificationAlert ref={myRef} />
      </div>
    </>
  );
};

export default EmployeeDashboard;
